import PropTypes from 'prop-types';
import { useState } from 'react';
import { CalendarGrid } from './components/CalendarGrid';
import { DayHeader } from './components/DayHeader';
import { DeleteButton } from './components/DeleteButton';
import { TimeHeader } from './components/TimeHeader';
import { useBlockDragHandlers } from './hooks/useBlockDragHandlers';
import { useBlockModal } from './hooks/useBlockModal';
import { useBlockRendering } from './hooks/useBlockRendering';
import { useCalendarScales } from './hooks/useCalendarScales';
import { useCalendarTime } from './hooks/useCalendarTime';
import { useEventInfo } from './hooks/useEventInfo';
import { useTimePeriods } from './hooks/useTimePeriods';
import ModalForm from './modalForm/ModalForm';
import { RefRegistry } from './ref-registry';

function LdenCalendar({
    events = [],
    measuringPointName,
    data,
    inViewStartDate,
    dayStartOffset,
    timezone,
    unit,
    loading,
    currentDate,
    changeTemplate,
    customTemplate,
    buildMode,
    deleteCustomTemplate,
}) {
    const { days, timeColumns } = useCalendarTime(inViewStartDate, timezone, dayStartOffset);
    const { lastContactDate, lastRecordDate, lastRecordUploads } = useEventInfo(events, timezone);
    const { graphGridRef, scalesPerDay } = useCalendarScales(days);
    const timePeriods = useTimePeriods();

    const [currentBlock, setCurrentBlock] = useState();
    const [drawingBlock, setDrawingBlock] = useState();

    const refRegistry = RefRegistry.use();

    const { showModal, updateBlock, deleteBlock, openEditModal, closeModal } = useBlockModal({
        changeTemplate,
        setCurrentBlock,
    });

    const { blockDragStart, blockDragMove, blockDragEnd, onMouseLeave } = useBlockDragHandlers({
        customTemplate,
        setDrawingBlock,
        drawingBlock,
        changeTemplate,
        openEditModal,
        scalesPerDay,
    });

    const allBlocksPerDay = useBlockRendering({
        scalesPerDay,
        timezone,
        data,
        currentDate,
        unit,
        buildMode,
        lastContactDate,
        lastRecordDate,
        lastRecordUploads,
        measuringPointName,
        drawingBlock,
        openEditModal,
        currentBlock,
        refRegistry,
    });

    return (
        <div className="w-full py-4 text-[9px]">
            <div className="flex">
                <DayHeader days={days} buildMode={buildMode} />
                <div className="w-full">
                    {buildMode && customTemplate && customTemplate.length > 0 && (
                        <DeleteButton onDelete={deleteCustomTemplate} />
                    )}
                    <TimeHeader timePeriods={timePeriods} timeColumns={timeColumns} />
                    <CalendarGrid
                        graphGridRef={graphGridRef}
                        days={days}
                        timeColumns={timeColumns}
                        loading={loading}
                        buildMode={buildMode}
                        blockDragStart={blockDragStart}
                        blockDragMove={blockDragMove}
                        blockDragEnd={blockDragEnd}
                        onMouseLeave={onMouseLeave}
                        allBlocksPerDay={allBlocksPerDay}
                    />
                    {showModal && (
                        <ModalForm
                            isOpen={showModal}
                            onClose={closeModal}
                            onSave={updateBlock}
                            onDelete={deleteBlock}
                            data={currentBlock}
                            customTemplate={customTemplate}
                            refRegistry={refRegistry}
                            dayStartOffset={dayStartOffset}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

LdenCalendar.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    inViewStartDate: PropTypes.object.isRequired,
    dayStartOffset: PropTypes.number.isRequired,
    timezone: PropTypes.string.isRequired,
    unit: PropTypes.string,
    currentDate: PropTypes.instanceOf(Date).isRequired,
    loading: PropTypes.bool,
    events: PropTypes.array,
    measuringPointName: PropTypes.string,
    buildMode: PropTypes.bool,
    changeTemplate: PropTypes.func,
    customTemplate: PropTypes.array,
    deleteCustomTemplate: PropTypes.func,
};

export default LdenCalendar;
